import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getDashboardProjects } from '../../features/project/projectService';
import ProjectOverview from '../../features/project/project/components/ProjectOverview';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/Button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/Table';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Home,
  Loader2,
  Search,
  Plus,
  Folder,
  Layers,
  Puzzle,
  AlertTriangle,
  TrendingUp,
  Edit,
  Check,
  Tag,
  Sparkles,
  Building,
  Boxes,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { RootState } from '@/store/types';

const ComingSoonOverlay = () => (
  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-[2px] rounded-[inherit]">
    <div className="flex flex-col items-center gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-widest text-neutral-400">
        Již brzy
      </span>
      <span className="text-[11px] text-neutral-400">Připravujeme pro vás</span>
    </div>
  </div>
);

const KPI = ({
  label,
  value,
  delta,
  trend,
  icon,
}: {
  label: string;
  value: string | number;
  delta?: string;
  trend?: 'up' | 'warn' | 'default';
  icon: React.ReactNode;
}) => (
  <Card className="!shadow-sm-soft">
    <div className="p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] uppercase tracking-wider font-medium text-neutral-500">
          {label}
        </span>
        <span className="h-7 w-7 rounded bg-neutral-50 text-neutral-700 flex items-center justify-center">
          {icon}
        </span>
      </div>
      <div className="flex items-baseline justify-between">
        <div className="text-3xl font-semibold tracking-tight tabular-nums">{value}</div>
        {delta && (
          <Badge
            variant={trend === 'up' ? 'success' : trend === 'warn' ? 'warning' : 'default'}
            size="md"
          >
            {trend === 'up' && <TrendingUp size={10} />}
            {trend === 'warn' && <AlertTriangle size={10} />}
            {delta}
          </Badge>
        )}
      </div>
    </div>
  </Card>
);

const Dashboard = (_props) => {
  const [data, setData] = useState<any>({ summary: null, projects: [] });
  const [loading, setLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const user = useSelector((state: RootState) => (state as any).auth?.user);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = () => {
    setLoading(true);
    getDashboardProjects('')
      .then((res) => {
        setData(res.data);
      })
      .catch((err) => {
        console.error('Failed to load dashboard data', err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (loading && !data.summary) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-neutral-500" />
      </div>
    );
  }

  if (selectedProjectId) {
    return (
      <div className="flex flex-col h-full overflow-hidden">
        <div className="p-6 pb-4 border-b">
          <Breadcrumb className="mb-4">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink
                  data-testid="dashboard-breadcrumb-home-link"
                  onClick={() => setSelectedProjectId(null)}
                  className="cursor-pointer flex items-center gap-1"
                >
                  <Home className="h-3.5 w-3.5" /> Nastenka
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Detail akce</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <Button
            data-testid="dashboard-back-btn"
            variant="outline"
            size="sm"
            onClick={() => setSelectedProjectId(null)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zpet na vyber akci
          </Button>
        </div>
        <div className="grow overflow-y-auto p-6">
          <ProjectOverview projectId={selectedProjectId} />
        </div>
      </div>
    );
  }

  const { summary, projects } = data;
  const now = new Date();
  const dayNames = ['Nedele', 'Pondeli', 'Utery', 'Streda', 'Ctvrtek', 'Patek', 'Sobota'];
  const dateStr = `${dayNames[now.getDay()]}, ${now.getDate()}. ${
    now.getMonth() + 1
  }. ${now.getFullYear()}`;
  const firstName = user?.first_name || 'uzivateli';
  const activeCount = summary?.total_active_projects ?? projects.length;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="px-8 py-8">
        {/* Greeting header */}
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-wider font-medium text-primary-600 mb-1">
              {dateStr}
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">Dobry den, {firstName}</h1>
            <p className="text-sm text-neutral-500 mt-0.5">
              Mate{' '}
              <span className="font-medium text-neutral-800">
                {activeCount} {activeCount === 1 ? 'akci' : activeCount < 5 ? 'akce' : 'akci'} v
                aktivni fazi
              </span>
              .
            </p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="dashboard-search-btn" variant="outline" size="sm">
              <Search size={14} /> Hledat
            </Button>
            <Button data-testid="dashboard-new-project-btn" size="sm">
              <Plus size={14} /> Nova akce
            </Button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <KPI label="Aktivni akce" value={activeCount} trend="up" icon={<Folder size={16} />} />
          <KPI
            label="Skladby celkem"
            value={summary?.total_typemarks ?? '-'}
            trend="up"
            icon={<Layers size={16} />}
          />
          <KPI
            label="Reseni (knihovna)"
            value={summary?.total_solutions ?? '-'}
            trend="up"
            icon={<Puzzle size={16} />}
          />
          <KPI
            label="K revizi"
            value={summary?.total_needs_review ?? '-'}
            trend="warn"
            icon={<AlertTriangle size={16} />}
          />
        </div>

        {/* Two-column main */}
        <div className="grid grid-cols-[1.6fr_1fr] gap-6">
          {/* Left: recent projects table + activity feed */}
          <div className="space-y-6">
            {/* Recent projects table */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Posledni akce</CardTitle>
                    <CardDescription>Naposledy upravene stavby a skladby</CardDescription>
                  </div>
                  <Button data-testid="dashboard-show-all-btn" variant="outline" size="sm">
                    Zobrazit vse <ArrowRight size={12} />
                  </Button>
                </div>
              </CardHeader>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Akce / Stavba</TableHead>
                    <TableHead>Stavba</TableHead>
                    <TableHead>Stav</TableHead>
                    <TableHead className="text-right">Rozpracovanost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projects.slice(0, 5).map((project: any) => (
                    <TableRow
                      key={project.id}
                      className="cursor-pointer"
                      onClick={() => setSelectedProjectId(project.id)}
                      data-testid={`dashboard-project-row-${project.id}`}
                    >
                      <TableCell className="font-medium">
                        {project.identifier} {project.title}
                      </TableCell>
                      <TableCell className="text-neutral-600">{project.building_title}</TableCell>
                      <TableCell>
                        <Badge variant="brand" size="sm">
                          Aktivni
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-xs text-neutral-500 font-mono">
                        {project.status_done_pct ?? 0}%
                      </TableCell>
                    </TableRow>
                  ))}
                  {projects.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-neutral-400">
                        Zadne aktivni akce
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>

            {/* Activity feed */}
            <Card className="relative overflow-hidden">
              <ComingSoonOverlay />
              <CardHeader>
                <CardTitle>Aktivita tymu</CardTitle>
                <CardDescription>Poslednich 24 hodin</CardDescription>
              </CardHeader>
              <div className="px-5 py-4 space-y-4">
                {[
                  {
                    who: 'MS',
                    name: 'Marie Sucha',
                    verb: 'vytvorila skladbu',
                    what: 'PD-03 Sdk akusticky',
                    when: 'pred 12 min',
                    icon: <Plus size={12} />,
                    type: 'add',
                  },
                  {
                    who: 'JN',
                    name: 'Jan Novak',
                    verb: 'upravil typemark',
                    what: 'PD-02 Podhled mineralni',
                    when: 'pred 1 hod',
                    icon: <Edit size={12} />,
                    type: 'edit',
                  },
                  {
                    who: 'PV',
                    name: 'Petr Vorel',
                    verb: 'schvalil reseni',
                    what: 'Plech nerezovy',
                    when: 'pred 3 hod',
                    icon: <Check size={12} />,
                    type: 'approve',
                  },
                  {
                    who: 'LK',
                    name: 'Lenka Kos',
                    verb: 'pridala vlastnost',
                    what: 'Pevnost v tahu',
                    when: 'vcera 17:42',
                    icon: <Tag size={12} />,
                    type: 'tag',
                  },
                ].map((a, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="relative shrink-0">
                      <div className="h-8 w-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[11px] font-semibold">
                        {a.who}
                      </div>
                      <div
                        className={cn(
                          'absolute -bottom-1 -right-1 h-4 w-4 rounded-full flex items-center justify-center border-2 border-white',
                          a.type === 'add' && 'bg-primary-600 text-white',
                          a.type === 'edit' && 'bg-amber-500 text-white',
                          a.type === 'approve' && 'bg-emerald-600 text-white',
                          a.type === 'tag' && 'bg-neutral-700 text-white'
                        )}
                      >
                        {a.icon}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-neutral-800">
                        <span className="font-medium">{a.name}</span>{' '}
                        <span className="text-neutral-500">{a.verb}</span>{' '}
                        <span className="font-medium">{a.what}</span>
                      </div>
                      <div className="text-xs text-neutral-500 mt-0.5">{a.when}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Right: side widgets */}
          <div className="space-y-6">
            {/* Stage progress */}
            <Card>
              <CardHeader>
                <CardTitle>Rozlozeni etap</CardTitle>
                <CardDescription>Aktivni akce dle etapy</CardDescription>
              </CardHeader>
              <div className="px-5 py-4 space-y-3">
                {[
                  ['Studie', 2, 'bg-neutral-300'],
                  ['DUR', 3, 'bg-primary-300'],
                  ['DSP', 5, 'bg-primary-500'],
                  ['DPS', 8, 'bg-primary-700'],
                  ['Realizace', 4, 'bg-emerald-500'],
                  ['Dokonceno', 18, 'bg-neutral-200'],
                ].map(([name, count, color]) => {
                  const total = 40;
                  return (
                    <div key={name as string}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-neutral-700">{name}</span>
                        <span className="text-xs font-mono text-neutral-500 tabular-nums">
                          {count}
                        </span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-neutral-100 overflow-hidden">
                        <div
                          className={cn('h-full rounded-full transition-all', color as string)}
                          style={{ width: `${((count as number) / total) * 100}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Pinned */}
            <Card className="relative overflow-hidden">
              <ComingSoonOverlay />
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Pripnute</CardTitle>
                  <Button data-testid="dashboard-pinned-add-btn" variant="ghost" size="icon-sm">
                    <Plus size={14} />
                  </Button>
                </div>
              </CardHeader>
              <div className="divide-y divide-neutral-100">
                {[
                  ['1234-test [DPS]', 'Skladby - 5 typemarku', <Folder size={14} key="f" />],
                  ['Knihovna materialu', 'Rezim cteni', <Boxes size={14} key="b" />],
                  ['Vinore BD - DPS', 'Aktivni projekt', <Building size={14} key="bu" />],
                ].map(([n, sub, icon], i) => (
                  <button
                    key={i}
                    data-testid={`dashboard-pinned-item-${i}-btn`}
                    className="w-full flex items-center gap-3 px-5 py-2.5 hover:bg-neutral-25 transition-colors text-left"
                  >
                    <span className="h-8 w-8 rounded bg-neutral-100 flex items-center justify-center text-neutral-700">
                      {icon}
                    </span>
                    <span className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{n}</div>
                      <div className="text-xs text-neutral-500">{sub}</div>
                    </span>
                    <ArrowUpRight size={12} className="text-neutral-400" />
                  </button>
                ))}
              </div>
            </Card>

            {/* Tip */}
            <div className="border border-primary-200 bg-primary-50/60 rounded p-4 flex gap-3">
              <div className="h-8 w-8 rounded bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                <Sparkles size={14} />
              </div>
              <div>
                <div className="text-sm font-medium text-primary-900">Novinka 2026.05</div>
                <p className="text-xs text-primary-800/80 mt-0.5">
                  Skladby nyni podporuji hromadne importovani vrstev z CSV.{' '}
                  <a
                    data-testid="dashboard-guide-link"
                    className="font-medium underline cursor-pointer"
                  >
                    Podivejte se na pruvodce.
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
